import React from 'react'
import { getTranslation } from '../translations'

function SectionBar({ title, subtitle }) {
  return (
    <div className="mb-2">
      <div className="bg-[#134225] text-white font-extrabold px-3 py-2 uppercase tracking-wide text-sm">
        {title}
      </div>
      {subtitle ? (
        <div className="text-[12px] italic text-[#334c3e] px-2 py-1">{subtitle}</div>
      ) : null}
    </div>
  )
}

const dotColor = (status) => {
  if (status === 'low') return 'bg-red-500';
  if (status === 'high') return 'bg-yellow-500';
  if (status === 'ok') return 'bg-green-500';
  return 'bg-gray-400';
}

function StatusDot({ status }) {
  return <span className={`inline-block h-3 w-3 rounded-full ${dotColor(status)}`} />
}

function HeaderRow({ t }) {
  return (
    <tr className="bg-[#134225] text-white text-sm">
      <th className="px-3 py-2 border border-[#d9e4dd] w-20 whitespace-nowrap text-center">{t.sNo}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.parameter}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.idealRange}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.actualValue}</th>
      <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.status}</th>
    </tr>
  )
}

// Decide status using numeric min/max; when missing, return 'na'
function calcStatus(actual, min, max) {
  if (actual == null || Number.isNaN(actual)) return 'na'
  if (min == null || max == null) return 'na'
  if (actual < min) return 'low'
  if (actual > max) return 'high'
  return 'ok'
}

function Row({ index, row }) {
  const status = calcStatus(row.actualNumber, row.min, row.max)
  const parameter = row.parameter ?? '-'
  const idealText = row.idealText ?? '-'
  const actualText = row.actualText ?? (row.actualNumber != null && !Number.isNaN(row.actualNumber) ? String(row.actualNumber) : '-')
  return (
    <tr className="odd:bg-white even:bg-gray-50 text-sm">
      <td className="px-3 py-2 border border-[#e4ebe7] text-center whitespace-nowrap">{index}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]">{parameter}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]">{idealText}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]">{actualText}</td>
      <td className="px-3 py-2 border border-[#e4ebe7]"><StatusDot status={status} /></td>
    </tr>
  )
}

export default function SoilAnalysis({ 
  title, 
  subtitle, 
  rows = [], 
  derivedRows = [],
  language = 'english'
}) {
  const t = getTranslation(language)
  const sectionTitle = title || t.section1Title
  const sectionSubtitle = subtitle || t.section1Subtitle
  
  return (
    <div className="mt-4">
      <SectionBar title={sectionTitle} subtitle={sectionSubtitle} />
      <div className="border border-[#cbd9d2] rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <HeaderRow t={t} />
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <Row key={r.parameter ?? i} index={i + 1} row={r} />
            ))}
          </tbody>
        </table>
      </div>

      {derivedRows.length > 0 && (
        <div className="mt-4">
          <SectionBar title={t.derivedParameters} />
          <div className="border border-[#cbd9d2] rounded-md overflow-hidden">
            <table className="w-full">
              <thead>
                <HeaderRow t={t} />
              </thead>
              <tbody>
                {derivedRows.map((r, i) => (
                  <Row key={r.parameter ?? `d-${i}`} index={rows.length + i + 1} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
